/**
 * ProShip Direct v2 — bulk rule upload.
 *
 * Two-step by design: read the file, show exactly what passed and what was rejected and why, and
 * only create rules when the merchant presses the button. Nothing is written on file select.
 *
 * That is the same shape as the serviceability upload, deliberately — these are the two places in
 * the module where a file changes how real orders ship, so they should behave identically.
 *
 * Uploaded rules land at the end of the list, because order decides which rule wins and putting
 * a batch of new rules ahead of hand-made ones would silently change existing allocation.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Alert, Table, TableBody, TableCell, TableHead, TableRow, Chip,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import MUIModal from '../../../common/MUIModal';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, SUCCESS, WARNING, BORDER, WHITE, LIGHT_BG } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { tHeadSx, tCellSx, statLabelSx } from './stats';
import { MONO } from './directCellStyles';
import { RULE_CSV_TEMPLATE, RULE_CSV_COLUMNS, parseRuleCsv } from '../data/ruleCsv';
import { downloadCsv } from '../data/serviceabilityUpload';
import { PRIORITY_STRATEGIES } from '../data/defaults';

const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const nameSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

const countChipSx = (color) => ({
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  fontWeight: WEIGHT.semibold,
  height: 20,
  borderRadius: '4px',
  bgcolor: `${color}14`,
  color,
});

export default function BulkRuleUploadModal({ existingNames, courierCodes, onCreate, onClose }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [report, setReport] = useState(null);
  const [readError, setReadError] = useState(null);

  const strategyLabel = (key) => PRIORITY_STRATEGIES.find((s) => s.key === key)?.label || key;

  const summary = useMemo(() => {
    if (!report) return null;
    return { valid: report.valid.length, rejected: report.rejected.length };
  }, [report]);

  const onFile = (file) => {
    setFileName(file.name);
    setReadError(null);
    const reader = new FileReader();
    reader.onload = () => setReport(parseRuleCsv(String(reader.result || ''), existingNames, courierCodes));
    reader.onerror = () => setReadError(`Could not read ${file.name}.`);
    reader.readAsText(file);
  };

  const create = () => {
    report.valid.forEach((rule) => onCreate(rule));
    onClose();
  };

  return (
    <MUIModal
      open
      handleClose={onClose}
      maxWidth='md'
      title='Bulk upload rules'
      action={
        <Box sx={{ display: 'flex', gap: GAP.sm }}>
          <Button
            size='small'
            sx={secondaryBtnSx}
            startIcon={<GetAppIcon sx={{ fontSize: 15 }} />}
            onClick={() => downloadCsv('courier-rules-template.csv', RULE_CSV_TEMPLATE)}
          >
            Download template
          </Button>
          <Button
            size='small'
            variant='contained'
            sx={primaryBtnSx}
            disabled={!summary || summary.valid === 0}
            onClick={create}
          >
            {summary?.valid ? `Create ${summary.valid} rule${summary.valid === 1 ? '' : 's'}` : 'Create rules'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        <Box>
          <Typography sx={{ ...statLabelSx, mb: 0.5 }}>The file</Typography>
          <Box sx={{ p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
            <Typography sx={{ ...subSx, mb: 0.75 }}>
              One rule per row, in template column order. Use <strong>|</strong> to separate
              multiple values in one cell — never a comma.
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: SIZE['4xs'], color: SUBTLE, wordBreak: 'break-all' }}>
              {RULE_CSV_COLUMNS.join(',')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: GAP.sm, mt: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              size='small'
              variant='contained'
              sx={primaryBtnSx}
              startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => fileRef.current?.click()}
            >
              Choose CSV
            </Button>
            <Typography sx={subSx}>{fileName || 'No file selected'}</Typography>
            <input
              ref={fileRef}
              type='file'
              accept='.csv,.txt'
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFile(file);
                e.target.value = '';
              }}
            />
          </Box>
        </Box>

        {readError && (
          <Alert severity='error' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0 }}>
            {readError}
          </Alert>
        )}

        {summary && (
          <>
            <Box sx={{ display: 'flex', gap: GAP.sm, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                size='small'
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                label={`${summary.valid} ready`}
                sx={countChipSx(SUCCESS)}
              />
              {summary.rejected > 0 && (
                <Chip
                  size='small'
                  icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                  label={`${summary.rejected} rejected`}
                  sx={countChipSx(WARNING)}
                />
              )}
              <Typography sx={subSx}>Nothing is created until you press Create.</Typography>
            </Box>

            {report.valid.length > 0 && (
              <Box>
                <Typography sx={{ ...statLabelSx, mb: 0.5 }}>Will be created</Typography>
                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflowX: 'auto', bgcolor: WHITE }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...tHeadSx, minWidth: 180 }}>Rule Name</TableCell>
                        <TableCell sx={{ ...tHeadSx, minWidth: 110 }}>Allocation</TableCell>
                        <TableCell sx={{ ...tHeadSx, minWidth: 70 }}>Conditions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.valid.map((rule) => (
                        <TableRow key={rule.name} hover>
                          <TableCell sx={tCellSx}>
                            <Typography sx={nameSx}>{rule.name}</Typography>
                          </TableCell>
                          <TableCell sx={tCellSx}>{strategyLabel(rule.strategy)}</TableCell>
                          <TableCell sx={tCellSx}>
                            {rule.conditions.length || 'Every order'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}

            {report.rejected.length > 0 && (
              <Box>
                <Typography sx={{ ...statLabelSx, mb: 0.5 }}>Rejected, and why</Typography>
                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflowX: 'auto', bgcolor: WHITE }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...tHeadSx, width: 60 }}>Row</TableCell>
                        <TableCell sx={{ ...tHeadSx, minWidth: 150 }}>Rule Name</TableCell>
                        <TableCell sx={{ ...tHeadSx, minWidth: 260 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.rejected.map((r) => (
                        <TableRow key={`${r.row}-${r.name}`} hover>
                          <TableCell sx={{ ...tCellSx, fontFamily: MONO }}>{r.row}</TableCell>
                          <TableCell sx={tCellSx}>{r.name}</TableCell>
                          <TableCell sx={{ ...tCellSx, color: WARNING, whiteSpace: 'normal' }}>{r.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <Typography sx={{ ...subSx, mt: 0.5 }}>
                  Rejected rows are skipped, not guessed at. Fix them in the file and upload again.
                </Typography>
              </Box>
            )}

            {summary.valid > 0 && (
              <Typography sx={subSx}>
                New rules are added at the end of the list, so they cannot change how existing
                rules allocate. Reorder them afterwards if one should win earlier.
              </Typography>
            )}
          </>
        )}
      </Box>
    </MUIModal>
  );
}
